/**
 * Referral pieces used outside the Invite page (docs/REFERRALS.md):
 * - ReferralCelebration: the big "Maria's Tacos just signed up with your invite!" / "2 free months added!" card, shown
 *   the next time the referrer opens the app, once (marked seen as soon as it shows). The whoosh is skipped when the
 *   person asked for reduced motion: same card, no animation.
 * - ReferralConsent: the one line in setup, "Share your setup progress with Ana, who invited you, so they can help".
 * - ReferralShareSetting: the same switch in Settings, changeable any time.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ReferralNoticeView, OrgReferral } from "../api.ts";
import { prefersReducedMotion, storedRef, type StoredRef } from "../lib/referrals.ts";
import { useApi } from "../session.tsx";
import { Button, Card, Section } from "./ui.tsx";

export function ReferralCelebration() {
  const api = useApi();
  const nav = useNavigate();
  const [notes, setNotes] = useState<ReferralNoticeView[]>([]);
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    let live = true;
    api.referral().then((r) => {
      if (!live || !r.notices.length) return;
      setNotes(r.notices);
      // Shown once: marked seen as soon as it is on screen.
      void api.seenReferralNotices(r.notices.map((n) => n.id)).catch(() => {});
    }).catch(() => { /* no referral data: nothing to celebrate */ });
    return () => { live = false; };
  }, [api]);
  const n = notes[0];
  if (!n) return null;
  const still = prefersReducedMotion();
  return (
    <div className="celebrate-wrap" role="status" aria-live="polite">
      <Card className={`celebrate${still ? "" : " celebrate-whoosh"}`} edge="go">
        <p className="kicker">{n.kind === "counted" ? "Free months" : "Your invite worked"}</p>
        <h2 className="celebrate-title">{n.title}</h2>
        <p>{n.body}</p>
        <div className="row wrap">
          <Button onClick={() => { setNotes([]); nav("/invite-business"); }}>See my invites</Button>
          <Button variant="ghost" onClick={() => setNotes(notes.slice(1))}>Close this card</Button>
        </div>
      </Card>
    </div>
  );
}

/** Setup's consent line. Default on. Renders nothing when the person didn't come from an invite link. */
export function useReferralConsent(): { ref: (StoredRef & { shareProgress: boolean }) | null; line: React.ReactNode } {
  const api = useApi();
  const [ref] = useState(() => storedRef());
  const [first, setFirst] = useState<string | null>(null);
  const [share, setShare] = useState(true);
  useEffect(() => {
    if (!ref) return;
    let live = true;
    api.referrerOf(ref.code).then((r) => { if (live) setFirst(r.firstName); }).catch(() => {});
    return () => { live = false; };
  }, [api, ref]);
  if (!ref) return { ref: null, line: null };
  const who = first ?? "the person";
  return {
    ref: { ...ref, shareProgress: share },
    line: (
      <label className="check consent">
        <input type="checkbox" role="switch" checked={share} onChange={(e) => setShare(e.target.checked)} />
        <span>Share your setup progress with {who}, who invited you, so they can help. Only five yes/no steps (location, sanitizer, team, first check, first clean week), never your logs or people. You can change this in Settings.</span>
      </label>
    ),
  };
}

export function ReferralShareSetting({ orgId }: { orgId: string }) {
  const api = useApi();
  const [r, setR] = useState<OrgReferral>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    let live = true;
    api.orgReferral(orgId).then((x) => { if (live) setR(x); }).catch(() => {});
    return () => { live = false; };
  }, [api, orgId]);
  if (!r?.referred) return null;
  const who = r.referrerFirstName ?? "The person who invited you";
  return (
    <Section id="invited-by" title="Who invited you" kicker="Sharing">
      <Card className="stack-sm">
        <label className="check">
          <input type="checkbox" role="switch" checked={!!r.shareProgress} onChange={async (e) => {
            setError(undefined);
            try { setR(await api.setOrgReferralShare(orgId, e.target.checked)); } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
          }} />
          <span>Share your setup progress with {who}, so they can help</span>
        </label>
        <p className="small muted">{r.shareProgress ? `${who} sees five yes/no steps and your free trial days.` : `${who} sees only your free trial and paid-month days.`} Never your logs, readings, reports or people.</p>
        {error && <p className="small bad">{error}</p>}
      </Card>
    </Section>
  );
}
